import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Calculator} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

export default function StraightLine() {
  const [cost, setCost] = useState("")
  const [salvage, setSalvage] = useState("")
  const [life, setLife] = useState("")
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{ annual: number; rate: number; totalDepr: number } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const s = parseFloat(salvage) || 0
    const l = parseInt(life)
    if (isNaN(c) || c <= 0) { setError("Please enter a valid asset cost."); return }
    if (isNaN(l) || l <= 0) { setError("Please enter a valid useful life (years)."); return }
    if (s < 0) { setError("Salvage value cannot be negative."); return }
    if (s >= c) { setError("Salvage value must be less than asset cost."); return }

    const annual = (c - s) / l
    const rows: ScheduleRow[] = []
    let accumulated = 0
    for (let i = 1; i <= l; i++) {
      accumulated += annual
      rows.push({
        year: i,
        beginningValue: c - accumulated + annual,
        depreciation: annual,
        accumulatedDepreciation: accumulated,
        endingBookValue: Math.max(s, c - accumulated),
      })
    }
    setSchedule(rows)
    setResults({ annual, rate: (annual / c) * 100, totalDepr: c - s })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-600 rounded-lg p-2">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">Core Method</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Straight Line Method Amortization Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate straight line depreciation instantly. Enter your asset cost, salvage value, and
            useful life to generate a full depreciation schedule you can export to Excel.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter Asset Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Cost ($)</label>
              <input
                type="number" value={cost} onChange={(e) => setCost(e.target.value)}
                placeholder="e.g. 50000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salvage Value ($)</label>
              <input
                type="number" value={salvage} onChange={(e) => setSalvage(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Useful Life (Years)</label>
              <input
                type="number" value={life} onChange={(e) => setLife(e.target.value)}
                placeholder="e.g. 10"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button
            onClick={calculate}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
          >
            Calculate Depreciation
          </button>

          {results !== null ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-blue-700">{fmt(results.annual)}</div>
                <div className="text-sm text-gray-600 mt-1">Annual Depreciation</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-gray-700">{results.rate.toFixed(2)}%</div>
                <div className="text-sm text-gray-600 mt-1">Depreciation Rate</div>
              </div>
              <div className="bg-green-50 rounded-xl p-5 text-center">
                <div className="text-2xl font-bold text-green-700">{fmt(results.totalDepr)}</div>
                <div className="text-sm text-gray-600 mt-1">Total Depreciable Amount</div>
              </div>
            </div>
          ) : null}

          {schedule.length > 0 ? (
            <DepreciationScheduleTable
              rows={schedule}
              onExport={() => exportToCSV(schedule, "straight-line-depreciation-schedule.csv")}
            />
          ) : null}
        </div>

        {/* SEO Content: Formula Section */}
        <div className="space-y-10 text-gray-700">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Straight Line Depreciation Formula</h2>
            <p className="mb-4">
              The straight line depreciation formula evenly distributes an asset's depreciable cost
              over its useful life. It is the simplest and most widely accepted method in both GAAP
              and IFRS accounting.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 font-mono text-blue-900 text-sm">
              <div className="font-bold mb-2">Annual Depreciation =</div>
              <div className="text-lg">(Asset Cost − Salvage Value) ÷ Useful Life</div>
              <div className="mt-3 text-blue-700">
                <strong>Example:</strong> ($50,000 − $5,000) ÷ 10 years = <strong>$4,500 per year</strong>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Salvage Value Formula</h2>
            <p className="mb-4">
              The <strong>salvage value</strong> (also called residual value or scrap value) is the
              estimated worth of an asset at the end of its <strong>useful life depreciation</strong>
              period. It reduces the total depreciable base.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 font-mono text-sm text-gray-800">
              <div>Depreciable Base = Asset Cost − Salvage Value</div>
              <div className="mt-2 text-gray-600">
                Example: $50,000 − $5,000 = $45,000 depreciable base
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              IRS rules generally do not allow you to depreciate an asset below its estimated
              salvage value. For MACRS purposes, salvage value is treated as zero.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Calculate Accumulated Depreciation</h2>
            <p className="mb-4">
              <strong>Accumulated depreciation</strong> is the total depreciation recognized from
              the asset's in-service date to any given point in time. It is a contra-asset account
              on the balance sheet.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 font-mono text-sm text-gray-800 mb-4">
              <div>Accumulated Depreciation = Annual Depreciation × Number of Years Elapsed</div>
              <div className="mt-2 text-gray-600">
                Example after Year 3: $4,500 × 3 = $13,500 accumulated
              </div>
            </div>
            <p>Book Value at any point = Asset Cost − Accumulated Depreciation</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Rates of Depreciation — Straight Line Chart
            </h2>
            <p className="mb-4">
              The depreciation rate under the straight line method is constant each year. Below is a
              <strong> Straight Line Depreciation Chart</strong> showing common rates by useful life:
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Useful Life</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">SL Rate / Year</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Common Asset Classes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["3 years", "33.33%", "Small tools, racehorses"],
                    ["5 years", "20.00%", "Computers, vehicles, research equipment"],
                    ["7 years", "14.29%", "Office furniture, most business equipment"],
                    ["10 years", "10.00%", "Certain farm buildings, water transportation"],
                    ["15 years", "6.67%", "Land improvements, shrubbery"],
                    ["20 years", "5.00%", "Farm buildings, utilities infrastructure"],
                    ["27.5 years", "3.64%", "Residential rental real estate"],
                    ["39 years", "2.56%", "Commercial real estate, nonresidential buildings"],
                  ].map(([life, rate, assets], i) => (
                    <tr key={life} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3 font-medium text-blue-700">{life}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{rate}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{assets}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Useful Life Depreciation Explained</h2>
            <p className="mb-3">
              <strong>Useful life</strong> is the estimated period over which a business expects to
              use an asset productively. The IRS provides standard useful lives under MACRS. Taxpayers
              using GAAP may estimate their own useful lives based on physical wear and economic factors.
            </p>
            <p className="text-sm text-gray-600">
              Key factors that determine useful life include: physical deterioration, technological
              obsolescence, legal limitations, and economic conditions. Choosing the correct
              useful life is critical for accurate IRS Form 4562 filing.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  )
}
